import type { IotAxiosRequestConfig } from "@core/data/infrastructures/http/axios-http-config.ts";
import { AxiosHttpConfig } from "@core/data/infrastructures/http/axios-http-config.ts";
import { HttpFailedRequestError } from "@core/domain/infrastructures/http/errors/http-failed-request.error.ts";
import { HttpRejectedRequestError } from "@core/domain/infrastructures/http/errors/http-rejected-request.error.ts";
import { HttpTooManyRequestsError } from "@core/domain/infrastructures/http/errors/http-too-many-requests.error.ts";
import { HttpUnauthorizedError } from "@core/domain/infrastructures/http/errors/http-unauthorized.error";
import { HttpUnprocessableContentError } from "@core/domain/infrastructures/http/errors/http-unprocessable-content.error.ts";
import type { HttpError } from "@core/domain/infrastructures/http/errors/http.error.ts";
import type { HttpConfig } from "@core/domain/infrastructures/http/http-config.ts";
import type { HttpErrorResponse } from "@core/domain/infrastructures/http/http-error-response.ts";
import type { HttpResponse } from "@core/domain/infrastructures/http/http-response.ts";
import type {
    Http,
    HttpOptions,
} from "@core/domain/infrastructures/http/http.ts";
import { UnauthorizedEventBus } from "@core/domain/models/event-bus.model.ts";
import type { EventBusRepository } from "@core/domain/repositories/event-bus.repository";
import { coreTypes } from "@core/root/core-types.di.ts";
import type { AxiosResponse, CreateAxiosDefaults } from "axios";
import axios, { Axios, AxiosError } from "axios";
import { merge } from "es-toolkit";
import { inject, injectable } from "inversify";
import {
    Err,
    err,
    errAsync,
    ok,
    okAsync,
    Result,
    ResultAsync,
} from "neverthrow";

@injectable()
export class AxiosHttp implements Http {
    private axiosInstance: Axios;

    private abortController: AbortController = new AbortController();

    constructor(
        @inject(AxiosHttpConfig)
        private readonly axiosHttpConfig: AxiosHttpConfig,
        @inject(coreTypes.repositories.EventBusRepository)
        private readonly eventBusRepository: EventBusRepository,
        private readonly options?: HttpOptions,
    ) {
        this.axiosInstance = axios.create({
            ...this.axiosHttpConfig.fromHttpConfig(
                this.options?.defaultRequestConfig,
            ),
            baseURL: this.options?.baseUrl,
            signal: this.abortController.signal,
            withCredentials: true, //TODO JON: ADJUST THIS
        });

        this.initResponseInterceptor();
    }

    private initResponseInterceptor(): void {
        this.axiosInstance.interceptors.response.use<
            // @ts-expect-error: This will be fixed in axios
            Result<HttpResponse<unknown>, HttpError>
        >(
            this.onResponseFulfilledInterceptor.bind(this),
            this.onResponseRejectedInterceptor.bind(this),
        );
    }

    private onResponseFulfilledInterceptor(
        response: AxiosResponse,
    ): Result<HttpResponse<unknown>, HttpError> {
        return ok<HttpResponse<unknown>, HttpError>({
            data: response.data,
            statusText: response.statusText,
            status: response.status,
        });
    }

    private onResponseRejectedInterceptor(
        error: unknown,
    ): Result<HttpResponse<unknown>, HttpError> | undefined {
        if (error instanceof AxiosError) {
            const axiosError: AxiosError<HttpErrorResponse> =
                error as AxiosError<HttpErrorResponse>;

            // Responded request but failed
            if (axiosError.response) {
                if (axiosError.response.status === HttpUnauthorizedError.CODE) {
                    const requestConfig: IotAxiosRequestConfig | undefined =
                        axiosError.config;
                    if (requestConfig?.emitUnauthorizedEvent !== false) {
                        this.eventBusRepository.emit(
                            new UnauthorizedEventBus(),
                        );
                    }
                    const unauthorizedErr = err(new HttpUnauthorizedError());
                    return (
                        this.options?.interceptors?.onResponseRejected?.(
                            unauthorizedErr,
                        ) ?? unauthorizedErr
                    );
                } else if (
                    axiosError.response.status === HttpTooManyRequestsError.CODE
                ) {
                    const tooManyRequestsErr = err(
                        new HttpTooManyRequestsError(axiosError.response.data),
                    );
                    return (
                        this.options?.interceptors?.onResponseRejected?.(
                            tooManyRequestsErr,
                        ) ?? tooManyRequestsErr
                    );
                } else if (
                    axiosError.response.status ===
                    HttpUnprocessableContentError.CODE
                ) {
                    const unprocessableErr = err(
                        new HttpUnprocessableContentError(
                            axiosError.response.data.error,
                        ),
                    );
                    return (
                        this.options?.interceptors?.onResponseRejected?.(
                            unprocessableErr,
                        ) ?? unprocessableErr
                    );
                }

                const failedRequestErr = err(
                    new HttpFailedRequestError(
                        axiosError.response.status,
                        undefined,
                        undefined,
                        axiosError.response.data,
                    ),
                );
                return (
                    this.options?.interceptors?.onResponseRejected?.(
                        failedRequestErr,
                    ) ?? failedRequestErr
                );
            }

            // Unresponded request (rejected)
            const rejectedRequestErr = err(
                new HttpRejectedRequestError(axiosError.message),
            );
            return (
                this.options?.interceptors?.onResponseRejected?.(
                    rejectedRequestErr,
                ) ?? rejectedRequestErr
            );
        } else if (
            error &&
            typeof error === "object" &&
            Object.hasOwn(error, "isErr") &&
            (error as Result<HttpResponse<unknown>, HttpError>).isErr()
        ) {
            // ONLY REACHABLE IF WE GOT AN ERROR WHEN OBTAINING A NEW ACCESS TOKEN
            const tokenErr = error as Err<HttpResponse<unknown>, HttpError>;
            return (
                this.options?.interceptors?.onResponseRejected?.(tokenErr) ??
                tokenErr
            );
        }

        return undefined;
    }

    private getSettingsObject(options?: HttpOptions): CreateAxiosDefaults {
        return {
            ...this.axiosHttpConfig.fromHttpConfig(
                options?.defaultRequestConfig,
            ),
            baseURL: options?.baseUrl,
            signal: this.abortController.signal,
            withCredentials: options?.secure,
        };
    }

    updateSettings(options?: Partial<HttpOptions>): void {
        merge(this.axiosInstance.defaults, this.getSettingsObject(options));
    }

    cancel(): void {
        this.abortController.abort();
    }

    get<R>(
        endpoint: string,
        config: HttpConfig | undefined,
    ): ResultAsync<HttpResponse<R>, HttpError> {
        // Once Either is removed from the project, this we need to use fromPromise and adjust ok/error handling
        return ResultAsync.fromSafePromise(
            this.axiosInstance.get<R, Result<HttpResponse<R>, HttpError>>(
                endpoint,
                this.axiosHttpConfig.fromHttpConfig(config),
            ),
        ).andThen((response) => {
            if (response.isErr()) {
                return errAsync(response.error);
            }
            return okAsync(response.value);
        });
    }

    post<R>(
        endpoint: string,
        body?: unknown,
        config?: HttpConfig,
    ): ResultAsync<HttpResponse<R>, HttpError> {
        return ResultAsync.fromSafePromise(
            this.axiosInstance.post<R, Result<HttpResponse<R>, HttpError>>(
                endpoint,
                body,
                this.axiosHttpConfig.fromHttpConfig(config),
            ),
        ).andThen((response) => {
            if (response.isErr()) {
                return errAsync(response.error);
            }
            return okAsync(response.value);
        });
    }

    put<R>(
        endpoint: string,
        body?: unknown,
        config?: HttpConfig,
    ): ResultAsync<HttpResponse<R>, HttpError> {
        return ResultAsync.fromSafePromise(
            this.axiosInstance.put<R, Result<HttpResponse<R>, HttpError>>(
                endpoint,
                body,
                this.axiosHttpConfig.fromHttpConfig(config),
            ),
        ).andThen((response) => {
            if (response.isErr()) {
                return errAsync(response.error);
            }
            return okAsync(response.value);
        });
    }

    patch<R>(
        endpoint: string,
        body?: unknown,
        config?: HttpConfig,
    ): ResultAsync<HttpResponse<R>, HttpError> {
        return ResultAsync.fromSafePromise(
            this.axiosInstance.patch<R, Result<HttpResponse<R>, HttpError>>(
                endpoint,
                body,
                this.axiosHttpConfig.fromHttpConfig(config),
            ),
        ).andThen((response) => {
            if (response.isErr()) {
                return errAsync(response.error);
            }
            return okAsync(response.value);
        });
    }

    delete(
        endpoint: string,
        config?: HttpConfig,
    ): ResultAsync<HttpResponse<never>, HttpError> {
        return ResultAsync.fromSafePromise(
            this.axiosInstance.delete<
                never,
                Result<HttpResponse<never>, HttpError>
            >(endpoint, this.axiosHttpConfig.fromHttpConfig(config)),
        ).andThen((response) => {
            if (response.isErr()) {
                return errAsync(response.error);
            }
            return okAsync(response.value);
        });
    }
}
